import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import {
  updateEducation,
  updateExperience,
  updatePersonal,
  updateProfession,
  updateProfileStatus
} from '../../reducers/profileSetupSlice';
import EducationDetails from './containers/Education/EducationDetails';
import ExperienceDetails from './containers/Experience/ExperienceDetails';
import PersonalDetailsComponent from './containers/Personal/PersonalDetailsComponent';
import { AddCategory } from './containers/Profession/AddCategory';
import { LOGOUT_ICON } from '../../assets/icons';
import authApi from '../../api/auth';
import { login } from '../../reducers/authSlice';
import { setUserProfile } from '../../reducers/profileSlice';
import {
  IProfessionHobby,
  IProfessionSkill,
  IProfessionSubject,
  ITutorDetailsStatus,
  ITutorEducationDetails,
  ITutorExperienceDetails,
  ITutorPersonalDetails,
  ITutorProfessionDetails
} from '@wot/typings';
import { toast } from 'react-toastify';

function ProfileSetup() {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const dispatch = useAppDispatch();
  const { request } = useAppSelector((state) => state.profileSetupSlice);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const onSubmitForm = async (profession: any) => {
    // if (request?.personal && request.education && request.experience && request.profession) {
    //   await dispatch(updateProfileStatus('under-review'));
    //   navigate('/dashboard');
    // } else {
    //   setError(true);
    // }
    if (!request?.personal || !request.education || !request.experience || !profession) {
      toast.error("Please fill out all the details");
      return;
    }
    await dispatch(updateProfileStatus('under-review'));
    navigate('/dashboard');
    return;
  };

  const onSavePersonal = async (data: ITutorPersonalDetails) => {
    setLoading(true);
    return await dispatch(updatePersonal(data))
      .then((res) => {
        setLoading(false);
        return res.payload;
      })
      .catch(() => setLoading(false));
  };

  const onSaveEducation = async (data: ITutorEducationDetails) => {
    setLoading(true);
    return await dispatch(updateEducation(data))
      .then((data) => {
        setLoading(false);
        return data.payload;
      })
      .catch(() => setLoading(false));
  };

  const onSaveExperience = async (data: ITutorExperienceDetails) => {
    setLoading(true);
    return await dispatch(updateExperience(data))
      .then((data) => {
        setLoading(false);
        return data.payload;
      })
      .catch(() => setLoading(false));
  };

  const onSaveProfession = async (
    data: ITutorProfessionDetails<IProfessionSubject | IProfessionHobby | IProfessionSkill>
  ) => {
    setLoading(true);
    console.log(data);
    return await dispatch(updateProfession(data))
      .then(async (data) => {
        setLoading(false);
        return data.payload;
      })
      .catch(() => setLoading(false));
  };

  const logout = async () => {
    await authApi.logout().catch(() => {
      return;
    });
    dispatch(login(null));
    dispatch(setUserProfile(null));
  };

  useEffect(() => {
    setError(false);
  }, [currentStep]);

  return (
    <div className="bg-lGray4 min-h-screen flex items-start justify-center">
      <div className="bg-white md:mt-10 w-full md:w-[80%] max-w-[1800px] md:h-[600px] flex flex-col md:flex-row rounded-xl md:border">
        <div className="hidden md:block w-1/4 py-12 bg-neutralBlue2 rounded-tl-xl rounded-bl-xl">
          <h5 className="px-6 font-medium text-xl">Complete setup</h5>
          <p className="px-6 py-1 text-xs text-neutralGray2">Complete and submit the form to get hired by students.</p>
          <p className="w-full px-6 py-2 mt-8 bg-neutralBlue1 font-medium">Profile</p>

          <div className="w-full px-6 relative z-20 mt-14 flex flex-col gap-7">
            <div className="ml-3 h-full absolute -z-10 border-l border-neutralGray2 border-dashed"></div>
            <Item
              text="Personal Details"
              status={request?.personal?.status || ''}
              stepNo={1}
              setCurrentStep={setCurrentStep}
              currentStep={currentStep}
            />
            <Item
              text="Education Details"
              status={request?.education?.status || ''}
              stepNo={2}
              setCurrentStep={setCurrentStep}
              currentStep={currentStep}
              prevStepStatus={request?.personal?.status || ''}
            />
            <Item
              text="Experience Details"
              status={request?.experience?.status || ''}
              stepNo={3}
              setCurrentStep={setCurrentStep}
              currentStep={currentStep}
              prevStepStatus={request?.education?.status || ''}
            />
            <Item
              text="Add Subjects"
              status={request?.profession?.status || ''}
              stepNo={4}
              setCurrentStep={setCurrentStep}
              currentStep={currentStep}
              prevStepStatus={request?.experience?.status || ''}
            />
          </div>
          <div onClick={() => logout()} className="mt-10 px-6 flex items-center gap-4 cursor-pointer">
            <img src={LOGOUT_ICON} className="w-4" />
            <p className="text-secondary text-sm font-medium">Logout</p>
          </div>
        </div>
        <div className="flex md:hidden flex-col gap-5 p-4">
          <div className='flex flex-col'>
            <h1 className="text-2xl font-semibold">Complete Profile</h1>
            <p className="py-1 text-sm text-neutralGray2">Complete and submit the form to get hired by students.</p>
          </div>
          <div className="flex flex-row w-full justify-between border-b border-black overflow-x-scroll no-scrollbar whitespace-nowrap lg:hidden">
            <Link
              to=""
              className={`px-4 py-2 text-sm font-medium ${currentStep === 1 ? ' border-b-[3px] border-secondary' : ''}`}
              onClick={() => setCurrentStep(1)}
            >
              Personal Details
            </Link>
            <Link
              to=""
              className={`px-4 py-2 text-sm font-medium ${currentStep === 2 ? ' border-b-[3px] border-secondary' : ''}`}
              onClick={() => setCurrentStep(2)}
            >
              Education Details
            </Link>
            <Link
              to=""
              className={`px-4 py-2 text-sm font-medium ${currentStep === 3 ? ' border-b-[3px] border-secondary' : ''}`}
              onClick={() => setCurrentStep(3)}
            >
              Experience Details
            </Link>
            <Link
              to=""
              className={`px-4 py-2 text-sm font-medium ${currentStep === 4 ? ' border-b-[3px] border-secondary' : ''}`}
              onClick={() => setCurrentStep(4)}
            >
              Profession Details
            </Link>
          </div>
        </div>
        <div className="relative w-full md:pt-10 flex flex-col px-4">
          {error && <p className="text-base text-red-600 font-medium px-10 py-2">Please Complete all the steps</p>}
          {currentStep === 1 ? (
            <PersonalDetailsComponent
              loading={loading}
              personal={request?.personal}
              onSave={onSavePersonal}
              onSaveAndNext={async (data) => {
                await onSavePersonal(data);
                setCurrentStep(2);
              }}
            />
          ) : currentStep === 2 ? (
            <EducationDetails
              loading={loading}
              education={request?.education}
              onSave={onSaveEducation}
              onSaveAndNext={async (data) => {
                await onSaveEducation(data);
                setCurrentStep(3);
              }}
            />
          ) : currentStep === 3 ? (
            <ExperienceDetails
              loading={loading}
              experience={request?.experience}
              onSave={onSaveExperience}
              onSaveAndNext={async (data) => {
                await onSaveExperience(data);
                setCurrentStep(4);
              }}
            />
          ) : (
            <AddCategory
              loading={loading}
              profession={request?.profession}
              onSave={onSaveProfession}
              onSaveAndNext={async (data) => {
                await onSaveProfession(data);
                await onSubmitForm(data);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface ItemProps {
  text: string;
  status: ITutorDetailsStatus | '';
  stepNo: number;
  setCurrentStep: any;
  currentStep: number;
  prevStepStatus?: ITutorDetailsStatus | '';
}

function Item({ text, status, stepNo, setCurrentStep, currentStep, prevStepStatus }: ItemProps) {
  return (
    <div
      key={stepNo}
      className={`flex flex-row items-center cursor-pointer gap-4 ${prevStepStatus === '' && 'cursor-not-allowed'} ${stepNo === currentStep && 'font-semibold'
        }`}
      onClick={() => {
        prevStepStatus !== '' && setCurrentStep(stepNo);
      }}
    >
      <div
        className={`hidden xl:flex xl:justify-center xl:p-1 xl:text-center xl:text-xs xl:w-6 xl:h-6 xl:rounded-full xl:shrink-0
        ${status === 'pending'
            ? 'bg-neutralGray1 text-white'
            : status === 'verified'
              ? 'bg-[#34C26D] text-white'
              : status === 'issue'
                ? 'bg-red-400 text-white'
                : 'bg-white text-black border border-black/75'
          }
      `}
      >
        {stepNo}
      </div>
      <p className="text-sm">{text}</p>
    </div>
  );
}

export default ProfileSetup;
